import { Injectable, Injector } from '@angular/core';
import { Response } from '@angular/http';
import { Observable } from 'rxjs';
import { SxHttpService } from './sxHttp.service';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import { CustomVocabulary, SystemVocabulary } from '../model/classes/resource.class';

export interface TranslationServiceRequest {
  endpoint: string;
  verb: 'get' | 'post' | 'put' | 'delete';
  payload?: any;
  currentBrowserCulture: string;
}

@Injectable()
export class SxTranslationService {
  socUrl: string = 'api/translationService';
  socWordType: number = 1;
  systemTranslationsUrl: string = 'systemTranslations';
  customTranslationsUrl: string = 'customTranslations';
  currentBrowserLang: string = window.navigator.language;
  languagesUrl: string = 'languages'
  wordTypesUrl: string = 'wordTypes'
  vocabularyUrl: string = 'vocabulary'

  private get _Http() { return this._injector.get(SxHttpService); }

  constructor(
    private _injector: Injector,
    private i18NextService: I18NextService,
    private i18nextPipe: I18NextPipe
  ) {
  }

  getSystemTermByCode(code: string, locale: String): Promise<any> {
    const req = <TranslationServiceRequest>{
      endpoint: `${this.systemTranslationsUrl}?code=${code}&language=${locale}`,
      verb: 'get',
    };
    return this._Http
      .post(`${this.socUrl}/Handle`, req)
      .map(response => {
        return response.json().data
      })
      .toPromise()
      .catch(this.handleError);
  }

  getAllSystemByLocale(locale: string): Promise<any> {
    const req = <TranslationServiceRequest>{
      endpoint: `${this.systemTranslationsUrl}?language=${locale}&wordTypeId=${this.socWordType}`,
      verb: 'get',
    };
    return this._Http
      .post(`${this.socUrl}/Handle`, req)
      .map(response => {
        return response.json().data.result
      })
      .toPromise()
      .catch(this.handleError);
  }

  getAllLanguages(): Promise<any> {
    const req = <TranslationServiceRequest>{
      endpoint: `${this.languagesUrl}`,
      verb: 'get'
    };
    return this._Http
      .post(`${this.socUrl}/Handle`, req)
      .map(response => {
        return response.json().data
      })
      .toPromise()
      .catch(this.handleError);
  }

  setServerLanguage(currentLang: string): Promise<any> {
    if(currentLang != null)
    this.currentBrowserLang = currentLang;

    const req = <TranslationServiceRequest>{
      currentBrowserCulture: this.currentBrowserLang
    };

    return this._Http
      .post(`${this.socUrl}/Handle`, req)
      .map(response => {
        return;
      })
      .toPromise()
      .catch(this.handleError);
  }

  getAllWordTypes(): Promise<any> {
    const req = <TranslationServiceRequest>{
      endpoint: `${this.wordTypesUrl}`,
      verb: 'get',
    };
    return this._Http
      .post(`${this.socUrl}/Handle`, req)
      .map(response => {
        return response.json().data
      })
      .toPromise()
      .catch(this.handleError);
  }

  async getVersions() {
    let promiseList: Promise<any>[] = [];

    let locale = window.navigator.language;

    promiseList.push(this.versionPromise(locale));

    if (locale.includes('-')) {
      promiseList.push(this.versionPromise(locale.split('-')[0]));
    }

    if (!locale.startsWith('en')) {
      promiseList.push(this.versionPromise('en'));
    }

    let versions = await Promise.all(promiseList);

    let dictionaryViaObject = new Object();

    versions.forEach(element => {
      try {
        dictionaryViaObject[element.locale] = element.version;
      } catch (error) {
        dictionaryViaObject[element.locale] = "noversion";
      }
    });

    return dictionaryViaObject;
  }

  private versionPromise(locale: string): Promise<any> {
    const req = <TranslationServiceRequest>{
      endpoint: `${this.systemTranslationsUrl}/vocabularyVersion?language=${locale}&wordTypeId=${this.socWordType}`,
      verb: 'get',
    };

    return this._Http
      .post(`${this.socUrl}/Handle`, req)
      .map(response => {
        return { locale: locale, version: response.json().data.result };
      })
      .toPromise()
      .catch(this.handleError);
  }

  getSelectedLanguage(): string {
    return this.i18NextService.language || window.localStorage.i18nextLng;
  }

  setLanguage(lang: any): string {
    return window.localStorage.i18nextLng = lang;
  }

  getResource(key: string, options: any = null): string {
    return this.i18nextPipe.transform(key, options);
  }

  getCustomVocabularies(organizationCode: string, systemCode: string, channelCode: string, page: number, pageSize: number, filterCode: string = null): Promise<any> {

    let endpoint = `${this.vocabularyUrl}/Custom/${organizationCode}/${systemCode}/${channelCode}?page=${page}&pageSize=${pageSize}`;

    if (filterCode) {
      endpoint += `&filterCode=${filterCode}`;
    }

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'get',
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }

  getCustomVocabulary(id: string): Promise<CustomVocabulary> {

    let endpoint = `${this.vocabularyUrl}/Custom/${id}/`;

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'get',
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }

  addCustomVocabulary(newCustomVocabulary: CustomVocabulary): Promise<string> {

    let endpoint = `${this.vocabularyUrl}/Custom`;

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'post',
      payload: newCustomVocabulary
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }

  updateCustomVocabulary(customVocabulary: CustomVocabulary): Promise<string> {

    let endpoint = `${this.vocabularyUrl}/Custom`;

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'put',
      payload: customVocabulary
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }

  deleteCustomVocabulary(id: string): Promise<string> {

    let endpoint = `${this.vocabularyUrl}/Custom?id=${id}`;

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'delete'
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }

  getSystemVocabularies(page: number, pageSize: number, filterCode: string = null, filterWordType: number = null): Promise<any> {

    let endpoint = `${this.vocabularyUrl}/System?page=${page}&pageSize=${pageSize}`;

    if (filterCode) {
      endpoint += `&filterCode=${filterCode}`;
    }

    if (filterWordType) {
      endpoint += `&filterWordType=${filterWordType}`;
    }

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'get',
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }

  getSystemVocabulary(id: string): Promise<SystemVocabulary> {

    let endpoint = `${this.vocabularyUrl}/System/${id}/`;

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'get',
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }

  updateSystemVocabulary(systemVocabulary: SystemVocabulary): Promise<string> {

    let endpoint = `${this.vocabularyUrl}/System`;

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'put',
      payload: systemVocabulary
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }

  private handleError(error: Response | any) {
    console.error(error);
    const result = {
      codigo: null,
      mensaje: error.toString(),
    };
    return Observable.throw(result);
  }

  serialize(isCustom: boolean, organizationCode: string, systemCode: string, channelCode: string): Promise<any> {
    let endpoint = isCustom ? `${this.customTranslationsUrl}/serialize?organizationCode=${organizationCode}&systemCode=${systemCode}&channelCode=${channelCode}`:
                              `${this.systemTranslationsUrl}/serialize`;

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'get',
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }

  export(isCustom: boolean, culture: string, organizationCode: string, systemCode: string, channelCode: string): Promise<any> {
    
    let endpoint = isCustom ? `${this.customTranslationsUrl}/export?language=${culture}&baseLanguaje=en&organizationCode=${organizationCode}&systemCode=${systemCode}&channelCode=${channelCode}`:
                              `${this.systemTranslationsUrl}/export?language=${culture}&baseLanguaje=en`;

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'get',
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }

  import(isCustom: boolean, file: any, culture: string, organizationCode: string, systemCode: string, channelCode: string): Promise<string> {

    let endpoint = isCustom ? `${this.customTranslationsUrl}/import?languaje=${culture}&organizationCode=${organizationCode}&systemCode=${systemCode}&channelCode=${channelCode}`:
                              `${this.systemTranslationsUrl}/import?languaje=${culture}`;

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'post',
      payload: file
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }

  deserialize(isCustom: boolean, source: any, organizationCode: string, systemCode: string, channelCode: string): Promise<string> {

    let endpoint = isCustom ? `${this.customTranslationsUrl}/deserialize?organizationCode=${organizationCode}&systemCode=${systemCode}&channelCode=${channelCode}`:
                              `${this.systemTranslationsUrl}/deserialize`;

    const req = <TranslationServiceRequest>{
      endpoint: endpoint,
      verb: 'post',
      payload: source
    };

    return this._Http
      .post(`${this.socUrl}/HandleSecure`, req)
      .map(response => {
        return response.json().data.result;
      })
      .toPromise()
      .catch(this.handleError);
  }


}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/infrastructure/services/translation-service.service.ts